import * as React from "react";
import { Upload, Search, X, Trash2 } from "lucide-react";
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadTrigger,
  FileUploadList,
  FileUploadItem,
  FileUploadItemPreview,
  FileUploadItemMetadata,
  FileUploadItemProgress,
  FileUploadItemDelete,
  useFileUpload,
  type FileUploadProps,
} from "@/shared/ui/components/ui/file-upload";
import { Button } from "./Button";
import { cn } from "@/shared/lib/utils";
import { UploadIcon } from "@/assets/svgs/uploadIcon";
import { Tooltip } from "./Tooltip";
import { useTranslation } from "react-i18next";

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

interface FileUploadItemWrapperProps {
  file: File;
}

function FileUploadItemWrapper({ file }: FileUploadItemWrapperProps) {
  const { t } = useTranslation();
  const fileState = useFileUpload((state) => state.files.get(file));

  if (!fileState) return null;

  const { status, progress, error } = fileState;
  const isUploading = status === "uploading";
  const isSuccess = status === "success";
  const isError = status === "error" || !!error;

  return (
    <FileUploadItem
      value={file}
      className={cn(
        "w-full min-w-full flex-wrap self-center flex items-center gap-3 rounded-lg border border-grey-200 bg-white p-3",
        isError && "border-error-700 bg-error-bg"
      )}
    >
      <div className="flex-shrink-0">
        <FileUploadItemPreview />
      </div>

      <div className="flex-1 min-w-0  ">
        <div className="flex flex-col gap-0.5">
          <Tooltip content={file.name}>
            <span className="text-xs t-medium text-grey-900 break-all sm:break-normal sm:truncate">
              {file.name}
            </span>
          </Tooltip>
          <span className="text-xs text-grey-500">
            {formatBytes(file.size)}
          </span>
          {isError && error && (
            <span className="text-xs text-error-700 mt-1">
              {t("common.fileFailedToUpload")}
            </span>
          )}
        </div>
        {isUploading && (
          <div className="mt-2 w-full">
            <FileUploadItemProgress forceMount className="h-1.5 w-full" />
          </div>
        )}
      </div>

      {isUploading && (
        <span className="text-xs font-bold text-grey-900 whitespace-nowrap flex-shrink-0">
          {Math.round(progress)}%
        </span>
      )}

      <div className="flex items-center gap-2 flex-shrink-0">
        {isUploading && (
          <FileUploadItemDelete className="p-1 hover:bg-grey-100 rounded cursor-pointer">
            <X className="w-4 h-4 text-grey-900" />
          </FileUploadItemDelete>
        )}

        <FileUploadItemDelete className="p-1 hover:bg-grey-100 rounded cursor-pointer">
          <Trash2 className="w-4 h-4 text-grey-900" />
        </FileUploadItemDelete>
      </div>
    </FileUploadItem>
  );
}

export interface FileUploaderProps extends Omit<FileUploadProps, "children"> {
  dropzoneTitle?: string;
  dropzoneDescription?: string;
  buttonLabel?: string;
  showFileCount?: boolean;
  showClearButton?: boolean;
  showError?: boolean;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  allowMultiple?: boolean;
}

const FileUploader = React.forwardRef<HTMLDivElement, FileUploaderProps>(
  (
    {
      dropzoneTitle = "Click to upload or drag and drop files here",
      dropzoneDescription,
      buttonLabel = "Browse files",
      showFileCount = true,
      showClearButton = true,
      showError = true,
      errorMessage,
      className,
      disabled = false,
      allowMultiple = true,
      value = [],
      onValueChange,
      multiple,
      ...props
    },
    ref
  ) => {
    const files = value || [];
    const [isHovered, setIsHovered] = React.useState(false);
    const isMultiple = allowMultiple && multiple !== false;

    return (
      <FileUpload
        ref={ref}
        value={value}
        onValueChange={onValueChange}
        className={cn("space-y-3", className)}
        disabled={disabled || (!isMultiple && files.length > 0)}
        multiple={isMultiple}
        {...props}
      >
        <FileUploadDropzone
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="w-full text-grey-400 hover:text-grey-900"
        >
          <UploadIcon
            color={disabled ? "#64615E" : isHovered ? "#070606" : "#64615E"}
            size={20}
          />
          <p className="text-sm ">{dropzoneTitle}</p>
          {dropzoneDescription && (
            <p className="text-xs ">{dropzoneDescription}</p>
          )}
          <FileUploadTrigger asChild>
            <Button
              className="mt-3 inline-flex items-center justify-center hover:bg-brand-green rounded-lg border border-grey-200 px-3 py-1.5 text-xs font-medium bg-brand-green text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={disabled || (!isMultiple && files.length > 0)}
            >
              <Search className="w-4 h-4" />
              {buttonLabel}
            </Button>
          </FileUploadTrigger>
        </FileUploadDropzone>

        <FileUploadList className="space-y-2 w-full">
          {files.map((file) => (
            <FileUploadItemWrapper key={file.name} file={file} />
          ))}
        </FileUploadList>

        {showFileCount && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-grey-400">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </span>
          </div>
        )}

        {showError && errorMessage && (
          <p className="text-xs text-error-700 mt-1 whitespace-pre-line rtl:text-right">
            {errorMessage}
          </p>
        )}
      </FileUpload>
    );
  }
);

FileUploader.displayName = "FileUploader";

export { FileUploader };
